/* @jsx h */
import { defineComponent, h, ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

import { EventCard } from "../Home/components/EventCard";
import { PublicHeaderComponent } from "../../components/PublicHeaderComponent";
import { PublicFooterComponent } from "../../components/PublicFooterComponent";
import { SeoHead } from "../../components/SeoHead";
import { CitySelector } from "../Home/components/CitySelector";
import { useBranding } from "../../providers/Branding";
import { EventWithShop } from "@lemmonhq/types";
import { fetchEvents } from "../../../api/client";
import { useLocalization } from "@lemmonhq/uikit";

export const AllEventsPage = defineComponent({
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { branding, defaultCity, cities: brandingCities } = useBranding();
    const { t } = useLocalization();
    const events = ref<EventWithShop[]>([]);
    const loading = ref(true);
    const selectedCity = ref(defaultCity.value || "lausanne");

    const loadEvents = async () => {
      try {
        loading.value = true;
        // TODO: Replace 'demo' with actual shop slug when available
        events.value = await fetchEvents(selectedCity.value);
      } catch (error) {
        console.error("Failed to load events:", error);
        events.value = [];
      } finally {
        loading.value = false;
      }
    };

    const handleCityChange = async (city: string) => {
      selectedCity.value = city;
      router.push({ path: `/events/${city}` });
      await loadEvents();
    };

    onMounted(async () => {
      const urlCity =
        (route.params.city as string) || defaultCity.value || "lausanne";
      selectedCity.value = urlCity;
      await loadEvents();
    });

    return () => (
      <div class="min-h-screen">
        <SeoHead
          title={t("customer.directory.events_seo_title", [
            selectedCity.value,
            branding.value?.name || "",
          ])}
          description={t("customer.directory.events_seo_description", [
            selectedCity.value,
          ])}
          keywords={`events ${selectedCity.value}, culinary events, dining experiences, food events`}
        />
        <PublicHeaderComponent>
          <CitySelector
            cities={brandingCities.value}
            selectedCity={selectedCity.value}
            onCityChange={handleCityChange}
          />
        </PublicHeaderComponent>

        <main class="max-w-7xl mx-auto px-4">
          {/* Page Header */}
          <div class="text-center py-12">
            <h1 class="text-4xl font-serif font-light text-gray-900 mb-4">
              {t("customer.directory.all_events_in", [selectedCity.value])}
            </h1>
            <p class="text-gray-600 font-light max-w-2xl mx-auto">
              {t("customer.directory.events_subtitle")}
            </p>
            <div class="w-16 h-px bg-gray-300 mx-auto mt-6"></div>
          </div>

          {/* Events List */}
          <div class="mb-16">
            {loading.value ? (
              <div class="text-center py-16">
                <div class="text-4xl mb-4">📅</div>
                <p class="text-gray-600">
                  {t("customer.directory.loading_events")}
                </p>
              </div>
            ) : events.value.length > 0 ? (
              <div class="space-y-4">
                {events.value.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    city={selectedCity.value}
                  />
                ))}
              </div>
            ) : (
              <div class="text-center py-16">
                <div class="text-6xl mb-6">📅</div>
                <h3 class="text-2xl font-serif font-light text-gray-900 mb-4">
                  {t("customer.directory.no_events_found")}
                </h3>
                <p class="text-gray-600 font-light max-w-md mx-auto">
                  {t("customer.directory.no_events_in_city", [
                    selectedCity.value,
                  ])}
                </p>
              </div>
            )}
          </div>
        </main>

        <PublicFooterComponent />
      </div>
    );
  },
});
